/* global alert */
import React, { Component } from 'react';
import moment from 'moment';
import { FormGroup, ControlLabel, FormControl, HelpBlock, Button, Col, Table } from 'react-bootstrap';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import firebase from '../helpers/firebase';
import { calculateTimeoffDuration, isSequentialDate } from '../helpers/utils';
import { checkAnnualPTOAmountLimit, checkCurrentPTOAmountLimit, checkNegativePTOAccruable, checkBalanceLegitimate } from '../helpers/timeoffRuleChecker';
import { TABLE_NAME, EMPLOYMENT, TIMEOFF, TIMEOFF_STATUS } from '../helpers/constants';
import BalanceTable from './BalanceTable';
import UserCombobox from './UserCombobox';
import { connect } from 'react-redux';

import 'react-day-picker/lib/style.css';

const startDayPickerProps = {
  todayButton: 'Go to Today',
  enableOutsideDays: true,
  modifiers: {
    monday: { daysOfWeek: [1] },
  },
};

const db = firebase.database();
const defaultTimeoffType = TIMEOFF.PTO.type;
const wfhRuleIndexAtRuleTable = 3;

const advNoticeRule = [
  {
    daysOffString: '11 days and more',
    minDaysOff: 11,
    maxDaysOff: 365,
    minAdvNoticeDays: 56,
    minAdvNoticeDaysString: '8 weeks'
  },
  {
    daysOffString: '4-10 days',
    minDaysOff: 4,
    maxDaysOff: 10,
    minAdvNoticeDays: 28,
    minAdvNoticeDaysString: '4 weeks'
  },
  {
    daysOffString: '2-3 days',
    minDaysOff: 2,
    maxDaysOff: 3,
    minAdvNoticeDays: 14,
    minAdvNoticeDaysString: '2 weeks'
  },
  {
    daysOffString: '1 day',
    minDaysOff: 1,
    maxDaysOff: 1,
    minAdvNoticeDays: 3,
    minAdvNoticeDaysString: '3 days'
  }
];

class TimeOffApplicationForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      editTimeoffId: this.props.match.params.id,
      startDate: moment().format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
      type: defaultTimeoffType,
      reason: '',
      isLoading: false,
      hours: 8,
      user: {},
      totalDays: 1,
      totalDaysNextYear: 0,
      newBalances: {},
      newBalancesNextYear: {}
    };

    this.setCurrentUser = this.setCurrentUser.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleBalanceChange = this.handleBalanceChange.bind(this);
    this.handleNextYearBalanceChange = this.handleNextYearBalanceChange.bind(this);
    this.handleSelectedUserChange = this.handleSelectedUserChange.bind(this);
    this.updateDays = this.updateDays.bind(this);
    this.handleSubstituteChange = this.handleSubstituteChange.bind(this);

    this.setCurrentUser();
  }

  setCurrentUser() {

    const user = this.props.user;

    if (this.props.admin) {
      this.setState({user});
    } else {
      firebase.database().ref('timeoffs')
        .orderByChild('userId').equalTo(user.id).once('value')
        .then(snapshot => {

          const timeoffHistory = snapshot.val();

          // set selected user ID
          if (this.props.match.params.id) {
            return firebase.database().ref('timeoffs')
              .child(this.props.match.params.id).once('value').then(snapshot => {
                const editTimeoff = snapshot.val();
                this.setState({
                  timeoffHistory,
                  selectedUserId: user.id,
                  selectedSubstituteUId: editTimeoff.substituteUId,
                  ...editTimeoff
                }, this.updateDays);
              });
          } else {
            this.setState({
              timeoffHistory,
              selectedUserId: user.id
            });
          }

        });
    }
  }

  handleSubmit(e) {
    e.preventDefault();

    let user = this.props.user;
    if (user.employment && user.employment !== EMPLOYMENT.FT.type) {
      const timeoffType = this.state.type;
      // part-timer can't apply for PTO, PSL, and HPSL
      if (timeoffType === TIMEOFF.PTO.type ||
          timeoffType === TIMEOFF.PSL.type ||
          timeoffType === TIMEOFF.HPSL.type) {
        alert(`This account can not apply for ${TIMEOFF[timeoffType.toUpperCase()].fullForm}.`);
        return;
      }
    }

    // check if balance is enough
    if (this.state.type === TIMEOFF.PTO.type) {

      if (checkCurrentPTOAmountLimit(this.state.newBalances, this.state.newBalancesNextYear, this.state.type)) {
        alert(`Your balance is not enough to submit this request.`);
        return;
      } else if (checkAnnualPTOAmountLimit(this.state.newBalances, this.state.newBalancesNextYear, this.state.type)) {
        alert(`You already applied pto amounts more than you had this year. Please check or cancel your previous pending/approved pto to process.`);
        return;
      } else if (checkNegativePTOAccruable(this.state.newBalances, this.state.type)) {
        const accumulatedDaysPerWeek = 10 / 52;
        const weeksToRecover = Math.round(Math.abs(this.state.newBalances[this.state.type].balance) / accumulatedDaysPerWeek);
        if (!window.confirm(`This will bring your PTO balance below zero, and it will take ${weeksToRecover} weeks to bring your balance back up to zero days of PTO. During these ${weeksToRecover} weeks you won't be able to take another PTO.`)) {
          return;
        }
      } else if (checkNegativePTOAccruable(this.state.newBalancesNextYear, this.state.type)) {
        const accumulatedDaysPerWeek = 10 / 52;
        const weeksToRecover = Math.round(Math.abs(this.state.newBalancesNextYear[this.state.type].balance) / accumulatedDaysPerWeek);
        if (!window.confirm(`This will bring your next year PTO balance below zero, and it will take ${weeksToRecover} weeks to bring your balance back up to zero days of PTO. During these ${weeksToRecover} weeks you won't be able to take another PTO.`)) {
          return;
        }
      }

    } else if (
      this.state.type === TIMEOFF.UPSL.type ||
      this.state.type === TIMEOFF.SPEC.type) {
      // do nothing
    } else if (checkBalanceLegitimate(this.state.newBalances, this.state.newBalancesNextYear, this.state.type)) {
      alert(`Your balance is not enough to submit this request.`);
      return;
    }

    if (!this.props.admin && (
      this.state.type === TIMEOFF.PTO.type ||
      this.state.type === TIMEOFF.UPTO.type)) {
      // check if the request is supposed to be merged with another request
      const timeoffs = this.state.timeoffHistory;
      for (var key in timeoffs) {
        let timeoff = timeoffs[key];
        if (this.state.editTimeoffId !== key &&
          timeoff.status !== TIMEOFF_STATUS.REJECTED &&
          timeoff.status !== TIMEOFF_STATUS.CANCELLED &&
          this.state.type === timeoff.type &&
          timeoff.days >= 1
        ) {
          let isSequentialWithPast = isSequentialDate(timeoff.endDate, this.state.startDate);
          let isSequentialWithFuture = isSequentialDate(this.state.endDate, timeoff.startDate);
          if (isSequentialWithPast || isSequentialWithFuture) {
            alert('you already have a ' +
            `${TIMEOFF[this.state.type.toUpperCase()].fullForm}` +
            ' request for the adjoining dates, please edit your existing request');
            return;
          }
        }
      }

      // check if there is enough days to process the request based on the policy
      let totalDaysToProcess = moment(this.state.startDate).diff(moment(new Date()), 'days');

      for (var i = 0; i < advNoticeRule.length; i++) {
        let rule = advNoticeRule[i];
        if (this.state.totalDays >= rule.minDaysOff && this.state.totalDays <= rule.maxDaysOff) {
          if (totalDaysToProcess < rule.minAdvNoticeDays) {
            alert(
              `You need to give at least ${rule.minAdvNoticeDaysString} notice ` +
              `to request ${this.state.totalDays} days of ` +
              (this.state.type === TIMEOFF.PTO.type ? TIMEOFF.PTO.fullForm : TIMEOFF.UPTO.fullForm)
            );
            return;
          }
        }
      }
    }

    // check rules for wfh.
    if (!this.props.admin && this.state.type === TIMEOFF.WFH.type) {
      let rule = advNoticeRule[wfhRuleIndexAtRuleTable];
      let totalDaysToProcess = moment(this.state.startDate).diff(moment(new Date()), 'days');

      if (totalDaysToProcess < rule.minAdvNoticeDays) {
        alert(
          `You need to give at least ${rule.minAdvNoticeDaysString} notice ` +
          `to request ${this.state.totalDays} days of ${TIMEOFF.WFH.fullForm}`
        );
        return;
      }
    }

    if (!this.state.selectedUserId) {
      return alert('User id is not set. Please refresh and try again.');
    }

    if (!this.state.selectedSubstituteUId) {
      return alert('Covering person is not set. Please refresh and try again.');
    }

    this.setState({
      isLoading: true
    });

    const data = {
      startDate: this.state.startDate,
      endDate: this.state.endDate,
      type: this.state.type,
      days: this.state.totalDays,
      daysNextYear: this.state.totalDaysNextYear,
      substituteUId: this.state.selectedSubstituteUId
    };

    if (this.state.editTimeoffId) {
      data.status = TIMEOFF_STATUS.PENDING;
      data.statusDate = moment().format('YYYY-MM-DD');
      data.modifiedBy = this.state.user;
      db.ref(TABLE_NAME.TIMEOFFS).child(this.state.editTimeoffId).update(data).then(() => {
        window.location.href = '/dashboard';
      });
    } else {

      if (this.props.admin) {
        data.statusDate = moment().format('YYYY-MM-DD');
        data.modifiedBy = this.state.user; // modified by admin (user)
        data.note = 'inputted by Admin';
      }
      data.status = (this.props.admin ? TIMEOFF_STATUS.APPROVED : TIMEOFF_STATUS.PENDING);
      data.requestDate = moment().format('YYYY-MM-DD');
      data.userId = this.state.selectedUserId;

      db.ref(TABLE_NAME.TIMEOFFS).push(data).then(() => {
        window.location.href = (this.props.admin ? '/admin' : '/dashboard');
      });
    }
  }

  updateDays(changes = {}) {
    const objTotalDays = calculateTimeoffDuration(
      changes.startDate || this.state.startDate,
      changes.endDate || this.state.endDate,
      changes.hours || this.state.hours
    );

    const totalDays = objTotalDays.totalDiffDays;
    const totalDaysNextYear = objTotalDays.diffDaysNextYear;

    Object.assign(changes, {
      totalDays,
      totalDaysNextYear,
      newBalances: {},
      newBalancesNextYear: {}
    });

    this.setState(changes);

  }

  handleDateChange(type, date, modifiers) {
    let changes = {
      [type]: date.format('YYYY-MM-DD')
    }

    if (type === 'startDate') {
      if (moment(this.state.endDate) < date) {
        changes.endDate = date.format('YYYY-MM-DD')
      }
    }

    this.updateDays(changes);
  }

  handleInputChange(e) {
    const name = e.target.name;
    const value = e.target.value;
    let changes = {[name]: value}
    this.updateDays(changes);
  }

  handleSelectedUserChange(newSelectedUserId) {
    this.setState({
      selectedUserId: newSelectedUserId
    });
  }

  handleBalanceChange(newBalances) {
    this.setState({newBalances});
  }

  handleNextYearBalanceChange(newBalancesNextYear) {
    this.setState({newBalancesNextYear});
  }

  handleSubstituteChange(newSubstituteUId) {
    this.setState({
      selectedSubstituteUId: newSubstituteUId
    })
  }

  render() {
    const userCombobox = (
      <UserCombobox
        onInputChange={this.handleSelectedUserChange}
        selectedUserId={this.state.selectedUserId}>
      </UserCombobox>
    );

    if (this.props.admin && !this.state.selectedUserId) {
      return (
        <form>
          <Col xs={12} md={6}>
            {userCombobox}
          </Col>
        </form>
      );
    }

    let isLoading = this.state.isLoading;

    if (!this.props.admin) {
      startDayPickerProps.disabledDays = {
        before: new Date(),
        after: moment().add(1, 'year').toDate(),
      };
    } else {
      startDayPickerProps.disabledDays = {};
    }

    const endDayPickerProps = {
      todayButton: 'Go to Today',
      disabledDays: {
        before: moment(this.state.startDate).toDate(),
        after: (this.state.type === TIMEOFF.WFH.type
          ? moment(this.state.startDate).endOf('month').toDate()
          : moment(this.state.startDate).add(3, 'month').toDate()),
      },
      enableOutsideDays: true,
      modifiers: {
        monday: { daysOfWeek: [1] },
      },
    };

    return (
      <form>
        <Col xs={12} md={6}>

          {this.props.admin
            ? <div>{userCombobox}</div>
            : <div></div>}

          {this.state.editTimeoffId
            ? <h1>Modify Time Off Request</h1>
            : <h1>Apply For Leave</h1>}

          <FormGroup controlId="type">
            <ControlLabel>Time Off Type</ControlLabel>
            <FormControl id="type"
              name="type"
              onChange={this.handleInputChange}
              value={this.state.type}
              componentClass="select"
              placeholder="select"
            >
              {
                Object.keys(TIMEOFF).map(key => {
                  let timeoff = TIMEOFF[key];
                  if (timeoff.type === TIMEOFF.UPTO.type) {
                    return <option value={timeoff.type} disabled={this.props.admin}>
                      {timeoff.fullForm}
                    </option>
                  } else {
                    return <option value={timeoff.type}>{timeoff.fullForm}</option>
                  }
                })
              }
            </FormControl>
            <HelpBlock>
              Type of leave you wish to apply for
            </HelpBlock>
          </FormGroup>

          <FormGroup controlId="startDate">
            <ControlLabel>Start Date</ControlLabel>

            <DayPickerInput
              value={this.state.startDate}
              className="form-control date"
              onDayChange={(date, modifiers) => this.handleDateChange('startDate', date, modifiers)}
              name="startDate"
              id="startDate"
              format="YYYY-MM-DD"
              dayPickerProps={startDayPickerProps}
            />

            <HelpBlock>
              The day your leave starts
            </HelpBlock>
          </FormGroup>

          <FormGroup controlId="endDate">
            <ControlLabel>End Date</ControlLabel>

            <DayPickerInput
              value={this.state.endDate}
              className="form-control date"
              onDayChange={(date, modifiers) => this.handleDateChange('endDate', date, modifiers)}
              name="endDate"
              id="endDate"
              format="YYYY-MM-DD"
              dayPickerProps={endDayPickerProps}
            />

            <HelpBlock>
              The day your leave ends
            </HelpBlock>
          </FormGroup>

          { this.state.startDate === this.state.endDate && (
            <FormGroup controlId="hours">
              <ControlLabel>Number of Hours</ControlLabel>
              <FormControl id="hours" name="hours" onChange={this.handleInputChange} componentClass="select" placeholder="select">
                <option defaultValue value="8">Full Day</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
              </FormControl>
              <HelpBlock>
                Hours of leave if you're applying for leave on the same day
              </HelpBlock>
            </FormGroup>
          ) }

          <UserCombobox
            title="Who's covering for you?"
            onInputChange={this.handleSubstituteChange}
            selectedUserId={this.state.selectedSubstituteUId}
            description="Choose a colleague who will cover your responsibilities while you're off">
          </UserCombobox>

          <Button type="submit"
            bsStyle="success"
            disabled={isLoading}
            onClick={!isLoading ? this.handleSubmit : null}
          >
            {isLoading ? 'Submitting...' : 'Submit'}
          </Button>
        </Col>
        <Col xs={12} md={6} >
          { moment().year() === moment(this.state.startDate).year() && (
            <div>
              <h4>{moment(this.state.startDate).format('YYYY')} Balances</h4>
              <BalanceTable
                onBalanceChange={this.handleBalanceChange}
                editTimeoffId={this.state.editTimeoffId}
                startDate={this.state.startDate}
                endDate={this.state.endDate}
                yearOfBalance={this.state.startDate}
                type={this.state.type}
                userId={this.state.selectedUserId}
                totalDays={this.state.totalDays}
                totalDaysNextYear={this.state.totalDaysNextYear}
              />
            </div>
          ) }

          { moment().year() !== moment(this.state.endDate).year() && (
            <div>
              <h4>{moment(this.state.endDate).format('YYYY')} Balances</h4>
              <BalanceTable
                onNextYearBalanceChange={this.handleNextYearBalanceChange}
                editTimeoffId={this.state.editTimeoffId}
                startDate={this.state.startDate}
                endDate={this.state.endDate}
                yearOfBalance={this.state.endDate}
                type={this.state.type}
                userId={this.state.selectedUserId}
                totalDays={this.state.totalDays}
                totalDaysNextYear={this.state.totalDaysNextYear}
              />
            </div>
          ) }

          {!this.props.admin
            ? <div>
              <h4>Minimum Advance Notice</h4>
              <AdvanceNoticeTable />
            </div>
            : <div></div>}

        </Col>
      </form>
    );
  }
}

function AdvanceNoticeTable() {
  const trs = advNoticeRule.slice(0).sort((a, b) => {
    return a.minDaysOff - b.minDaysOff;
  }).map((rule) => {
    return <tr key={rule.minDaysOff}>
      <td>{ rule.daysOffString }</td>
      <td>{ rule.minAdvNoticeDaysString }</td>
    </tr>
  });

  return <Table striped bordered condensed hover>
    <thead>
      <tr>
        <th>Day(s) of Vacation(s)</th>
        <th>Minimum advance notice</th>
      </tr>
    </thead>
    <tbody>
      {trs}
    </tbody>
  </Table>
}

const mapStateToProps = (state) => {
  const { user } = state.auth;
  return { user };
}

export default connect(mapStateToProps)(TimeOffApplicationForm);



// WEBPACK FOOTER //
// ./src/components/TimeOffApplicationForm.js